import { get, isNumber } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import { PortHelpers } from '@diagram/helpers/PortHelpers';

import {
    Component,
    Current,
    Diagram,
    DiagramComponentInstance,
    DiagramConnection,
    Power,
    Voltage,
    VoltageType,
} from 'models';

import { ConnectionHelpers } from '../helpers/ConnectionHelpers';
import { ComponentInstanceService } from './ComponentInstanceService';
import { ConnectionService } from './ConnectionService';
import { DiagramService } from './DiagramService';
import { DiagramSyncService } from './DiagramSyncService';
import { SelectionService } from './SelectionService';

type Port = DiagramConnection['from' | 'to']['port'];

export type PortOption = {
    port: {
        key: number;
        label: string;
        purpose: string;
    };
    connected: boolean;
    specifications: any;
    voltageType: VoltageType;
    availableVoltageTypes: VoltageType[];
    defaultVoltageType: VoltageType | null;
};

type GetOptionsProps = {
    diagram: Diagram;
    component?: Component;
    componentInstance: DiagramComponentInstance;
    connection?: DiagramConnection;
};

const PortService = {
    getPorts: ({
        component,
        componentInstance,
    }: {
        component?: Component;
        componentInstance: DiagramComponentInstance;
    }): Component['electrical']['ports'] | undefined => {
        const specifications = component || componentInstance?.specifications;

        return specifications?.electrical?.ports;
    },

    getVisiblePortNumber: (component: DiagramComponentInstance, port: number | null) => {
        const nbPorts = (get(component, `specifications.electrical.ports`) as any[])?.length ?? 0;
        const componentSelected = SelectionService.isSelectedComponentInstance(component.id);

        if (!componentSelected) return '';

        if (nbPorts < 2) return '';
        if (!isNumber(port)) return '';

        if (port < 20) {
            return String.fromCharCode(0x2460 + port);
        }

        return `${port + 1}`;
    },

    getOptions: ({ diagram, component, componentInstance, connection }: GetOptionsProps) => {
        const specifications = component || componentInstance?.specifications || {};
        const componentInstanceConnections = ConnectionService.getConnectionsForComponentInstance(
            diagram,
            componentInstance,
        );

        const options: PortOption[] = [];
        const ports = PortService.getPorts({ componentInstance, component });

        if (!ports) return options;

        const definition = ComponentInstanceHelpers.definition(componentInstance);
        const defaultVoltageType = definition.ports.defaultVoltageType ?? null;

        ports.forEach((port, index) => {
            let purpose = '';

            if ('purpose' in port && port.purpose) {
                purpose = ComponentInstanceHelpers.definition(port.purpose).name;
            }

            // The voltage types the component instance itself allows on this port.
            // A linked component instance lists its ports from the product catalog
            // component, which can differ from the specifications stored on the
            // instance. Only offer what can actually be applied to the instance.
            const availableVoltageTypes = PortHelpers.getVoltageTypeOptions(componentInstance, index);

            const push = (voltageType: 'AC' | 'DC') => {
                options.push({
                    port: {
                        key: index,
                        label: get(componentInstance, `configuration.ports.${index}.label`) || `Port ${index + 1}`,
                        purpose,
                    },
                    connected: PortService.isConnected({
                        componentInstanceId: componentInstance.id,
                        portKey: index,
                        connections: componentInstanceConnections,
                    }),
                    specifications: get(specifications, `electrical.ports.${index}.${voltageType}`),
                    voltageType,
                    availableVoltageTypes,
                    defaultVoltageType,
                });
            };

            const pushAC = () => {
                if ('AC' in port && port.AC.enabled && availableVoltageTypes.includes('AC')) {
                    push('AC');
                }
            };

            const pushDC = () => {
                if ('DC' in port && port.DC.enabled && availableVoltageTypes.includes('DC')) {
                    push('DC');
                }
            };

            if (defaultVoltageType === 'AC') {
                pushAC();
                pushDC();
            } else {
                pushDC();
                pushAC();
            }
        });

        return options;
    },

    updatePortWithEffects: ({
        port,
        componentInstance,
        connectionId,
    }: {
        port: Port;
        componentInstance: DiagramComponentInstance;
        connectionId: string;
    }) => {
        const diagram = DiagramSyncService.get();

        const connection = diagram.connections[connectionId];

        if (connection) {
            const { from } = connection;
            const voltageType = ConnectionService.getVoltageType(connection);
            const portIsFrom = componentInstance.id === from.componentInstanceId;

            // add port to connection
            ConnectionService.updatePort(connection, portIsFrom ? 'from' : 'to', port);

            // effect: set port voltage type (if not set) to connection voltage type
            if (voltageType) {
                PortService.updateVoltageType(componentInstance.id, port, voltageType);
            }
        }
    },

    getConnection: (componentInstance: DiagramComponentInstance, port: number | null) => {
        if (port === null) {
            return null;
        }

        const { connections } = DiagramService.get();

        return (
            Object.values(connections).find(({ from, to }) => {
                return (
                    (from.componentInstanceId === componentInstance.id && from.port === port) ||
                    (to.componentInstanceId === componentInstance.id && to.port === port)
                );
            }) || null
        );
    },

    isConnected: ({
        componentInstanceId,
        portKey,
        connections,
    }: {
        componentInstanceId: string;
        portKey: number;
        connections: DiagramConnection[];
    }): boolean => {
        return connections.some((connection) => {
            return (
                (connection.from.componentInstanceId === componentInstanceId && connection.from.port === portKey) ||
                (connection.to.componentInstanceId === componentInstanceId && connection.to.port === portKey)
            );
        });
    },

    findMatchingPorts: ({
        fromComponentInstance,
        toComponentInstance,
        preferredVoltageType,
    }: {
        fromComponentInstance: DiagramComponentInstance;
        toComponentInstance: DiagramComponentInstance;
        preferredVoltageType: VoltageType | null;
    }) => {
        const diagram = DiagramService.get();

        let matchingFromPort: PortOption | null = null;
        let matchingToPort: PortOption | null = null;

        const fromOptions = PortService.getOptions({
            diagram,
            componentInstance: fromComponentInstance,
        });

        const toOptions = PortService.getOptions({
            diagram,
            componentInstance: toComponentInstance,
        });

        let score = -1;
        const getScore = (fromPort: PortOption, toPort: PortOption) => {
            const { voltageType: fromVoltageType, availableVoltageTypes: fromAvailableVoltageTypes } = fromPort;

            const { voltageType: toVoltageType, availableVoltageTypes: toAvailableVoltageTypes } = toPort;

            // Rate option where voltage types exactly match first
            if (fromVoltageType && toVoltageType && fromVoltageType === toVoltageType) {
                return fromVoltageType === preferredVoltageType ? 3 : 2;
            }

            // Rate option where ports can support the same voltage type second
            const canSupportSameVoltageType = fromAvailableVoltageTypes.some((voltageType) =>
                toAvailableVoltageTypes.includes(voltageType),
            );
            if (canSupportSameVoltageType) return 1;

            // Exclude options where voltage types do not match
            if (fromVoltageType && toVoltageType && fromVoltageType !== toVoltageType) return -1;

            return 0;
        };

        fromOptions.forEach((fromPort) => {
            toOptions.forEach((toPort) => {
                if (fromPort.connected || toPort.connected) return;

                const newScore = getScore(fromPort, toPort);

                if (newScore > score) {
                    score = newScore;
                    matchingFromPort = fromPort;
                    matchingToPort = toPort;
                }
            });
        });

        let suggestedVoltageType: VoltageType | null = null;

        if (matchingFromPort && matchingToPort) {
            const suggestedVoltageTypeFromPorts = PortService.getSuggestedVoltageType({
                fromPort: matchingFromPort,
                toPort: matchingToPort,
            });

            if (suggestedVoltageTypeFromPorts) {
                // If there is a voltage type suggested from the ports, use it
                suggestedVoltageType = suggestedVoltageTypeFromPorts;
            } else {
                // If there is no voltage type suggested from the ports, use the voltage type that is used the most frequently
                suggestedVoltageType = PortService.getMajorityVoltageType([...fromOptions, ...toOptions]);
            }
        }

        return {
            fromPort: matchingFromPort as PortOption | null,
            toPort: matchingToPort as PortOption | null,
            suggestedVoltageType,
        };
    },

    resolveFreeEndpointPorts: ({
        from,
        to,
        fromComponentInstance,
        toComponentInstance,
        preferredVoltageType,
        voltageTypeOverride,
    }: {
        from: DiagramConnection['from'];
        to: DiagramConnection['to'];
        fromComponentInstance: DiagramComponentInstance;
        toComponentInstance: DiagramComponentInstance;
        preferredVoltageType: VoltageType | null;
        voltageTypeOverride?: VoltageType | null;
    }): { fromPort: PortOption | null; toPort: PortOption | null; suggestedVoltageType: VoltageType | null } => {
        const fromFixedPort = from.port;
        const toFixedPort = to.port;

        const onlyOneEndpointIsFixed = (fromFixedPort === null) !== (toFixedPort === null);

        if (!onlyOneEndpointIsFixed) {
            return PortService.findMatchingPorts({
                fromComponentInstance,
                toComponentInstance,
                preferredVoltageType,
            });
        }

        const diagram = DiagramService.get();

        const fromOptions = PortService.getOptions({ diagram, componentInstance: fromComponentInstance });
        const toOptions = PortService.getOptions({ diagram, componentInstance: toComponentInstance });

        const voltageTypeOfFixedPort = (
            componentInstance: DiagramComponentInstance,
            port: number | null,
        ): VoltageType | null =>
            port !== null ? (componentInstance.configuration.ports[port]?.voltageType ?? null) : null;

        const fixedVoltageType: VoltageType | null =
            voltageTypeOfFixedPort(fromComponentInstance, fromFixedPort) ??
            voltageTypeOfFixedPort(toComponentInstance, toFixedPort) ??
            voltageTypeOverride ??
            preferredVoltageType;

        const chooseByVoltage = (candidates: PortOption[]): PortOption | null =>
            candidates.find((option) => option.voltageType === fixedVoltageType) ??
            candidates.find(
                (option) => !!fixedVoltageType && option.availableVoltageTypes.includes(fixedVoltageType),
            ) ??
            candidates[0] ??
            null;

        const resolvePort = (options: PortOption[], fixedPort: number | null): PortOption | null =>
            fixedPort !== null
                ? chooseByVoltage(options.filter((option) => option.port.key === fixedPort))
                : chooseByVoltage(options.filter((option) => !option.connected));

        return {
            fromPort: resolvePort(fromOptions, fromFixedPort),
            toPort: resolvePort(toOptions, toFixedPort),
            suggestedVoltageType: fixedVoltageType,
        };
    },

    getSuggestedVoltageType: ({
        fromPort,
        toPort,
    }: {
        fromPort: PortOption;
        toPort: PortOption;
    }): VoltageType | null => {
        const {
            voltageType: fromVoltageType,
            availableVoltageTypes: fromAvailableVoltageTypes,
            defaultVoltageType: fromDefaultVoltageType,
        } = fromPort;

        const {
            voltageType: toVoltageType,
            availableVoltageTypes: toAvailableVoltageTypes,
            defaultVoltageType: toDefaultVoltageType,
        } = toPort;

        // 1. If the voltageType is already set and EQUAL on the fromPort and toPort, return it
        if (fromVoltageType && toVoltageType && fromVoltageType === toVoltageType) {
            return fromVoltageType;
        }

        const sharedVoltageTypes = fromAvailableVoltageTypes.filter((voltageType) =>
            toAvailableVoltageTypes.includes(voltageType),
        );

        // 2. If there is only 1 voltage type shared between the ports, the voltage type is determined and return it
        if (sharedVoltageTypes.length === 1) {
            return sharedVoltageTypes[0];
        }

        // 3. If there is a default voltage type set on either the fromPort or the toPort, return it
        if (fromDefaultVoltageType !== toDefaultVoltageType && (fromDefaultVoltageType || toDefaultVoltageType)) {
            return fromDefaultVoltageType || toDefaultVoltageType;
        }

        return null;
    },

    getMajorityVoltageType: (portOptions: PortOption[]): VoltageType | null => {
        const voltageTypeCount: { [key in VoltageType]: number } = {
            AC: 0,
            DC: 0,
        };

        portOptions.forEach(({ voltageType, connected }) => {
            if (voltageType) {
                voltageTypeCount[voltageType] += connected ? 2 : 1;
            }
        });

        if (voltageTypeCount.AC === 0 && voltageTypeCount.DC === 0) return null;

        return voltageTypeCount.DC >= voltageTypeCount.AC ? 'DC' : 'AC';
    },

    updateCurrent: (
        componentInstance: DiagramComponentInstance,
        portIndex: DiagramConnection['from' | 'to']['port'],
        voltageType: VoltageType,
        current: Current,
    ) => {
        ComponentInstanceService.update(componentInstance.id, {
            [`specifications.electrical.ports.${portIndex}.${voltageType}.current.nom`]: current.nom,
            [`specifications.electrical.ports.${portIndex}.${voltageType}.current.max`]: current.max,
        });
    },

    updateVoltage: (
        componentInstance: DiagramComponentInstance,
        portIndex: DiagramConnection['from' | 'to']['port'],
        voltageType: VoltageType,
        voltage: Voltage,
    ) => {
        ComponentInstanceService.update(componentInstance.id, {
            [`specifications.electrical.ports.${portIndex}.${voltageType}.voltage.min`]: voltage.min,
            [`specifications.electrical.ports.${portIndex}.${voltageType}.voltage.nom`]: voltage.nom,
            [`specifications.electrical.ports.${portIndex}.${voltageType}.voltage.max`]: voltage.max,
        });
    },

    updatePower: (
        componentInstance: DiagramComponentInstance,
        portIndex: DiagramConnection['from' | 'to']['port'],
        voltageType: VoltageType,
        power: Power,
    ) => {
        ComponentInstanceService.update(componentInstance.id, {
            [`specifications.electrical.ports.${portIndex}.${voltageType}.power.nom`]: power.nom,
            [`specifications.electrical.ports.${portIndex}.${voltageType}.power.max`]: power.max,
        });
    },

    updateVoltageType: (
        componentInstanceId: DiagramComponentInstance['id'],
        portIndex: DiagramConnection['from' | 'to']['port'],
        voltageType: VoltageType,
        withSideEffects: boolean = true,
    ) => {
        if (portIndex === null) {
            // Cannot set the voltage type of a port that does not exist
            return;
        }

        const diagram = DiagramSyncService.get();
        const componentInstance = ComponentInstanceService.getById(componentInstanceId);

        if (PortHelpers.isVoltageTypeAllowed(componentInstance, portIndex, voltageType)) {
            componentInstance.configuration.ports[portIndex].voltageType = voltageType;
        }

        if (!withSideEffects) {
            return;
        }

        const connection = ConnectionHelpers.getConnectionForComponentInstance(
            diagram,
            componentInstance.id,
            portIndex,
        );

        if (!connection) {
            return;
        }

        const otherEndpoint = ConnectionHelpers.getOtherEndpoint(diagram, componentInstance.id, portIndex);

        if (!otherEndpoint) {
            return;
        }

        const { componentInstanceId: otherComponentInstanceId, port: otherPortIndex } = otherEndpoint;
        // Only do this when its allowed.
        PortService.updateVoltageType(otherComponentInstanceId, otherPortIndex, voltageType, false);
    },
};

export { PortService };
